import React, { forwardRef, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { useMutation } from '@tanstack/react-query';
import { ImageIcon, RefreshCw, Upload } from 'lucide-react';

// API request function
async function apiRequest<T>(url: string, options?: RequestInit): Promise<T> {
  const token = localStorage.getItem('token');
  let newHeaders = { ...(options?.headers || {}) };
  
  if (token) {
    newHeaders = { ...newHeaders, 'Authorization': `Bearer ${token}` };
  }

  // Don't set Content-Type for FormData requests
  if (!(options?.body instanceof FormData)) {
    newHeaders = { ...newHeaders, 'Content-Type': 'application/json' };
  }
  
  const headers = newHeaders as Record<string, string>;

  const requestOptions: RequestInit = {
    ...options,
    headers
  };

  const res = await fetch(url, requestOptions);
  
  if (!res.ok) {
    const text = await res.text();
    try {
      const errJson = JSON.parse(text);
      throw new Error(errJson.message || `${res.status}: ${text}`);
    } catch (e) {
      throw new Error(`${res.status}: ${text || res.statusText}`);
    }
  }
  
  return res.json() as Promise<T>;
}

interface ImageUploadFieldProps {
  id: string;
  label?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  uploadEndpoint: string;
}

export const ImageUploadField = forwardRef<HTMLInputElement, ImageUploadFieldProps>(
  ({ id, label = "Image URL (Optional)", value, onChange, placeholder = "Enter image URL", uploadEndpoint }, ref) => {
    const { toast } = useToast();
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Handle file selection change
    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files[0]) {
        setSelectedFile(e.target.files[0]);
      }
    };

    // Mutation to upload an image
    const uploadImageMutation = useMutation({
      mutationFn: async (file: File) => {
        const formData = new FormData();
        formData.append('file', file);
        
        return apiRequest<{imageUrl: string}>(uploadEndpoint, {
          method: "POST",
          body: formData,
        });
      },
      onSuccess: (data) => {
        onChange(data.imageUrl);
        setSelectedFile(null);
        // Clear the file input
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        /**
         * 
         * 
         toast({
          title: "Image uploaded successfully",
          description: "The image has been uploaded and the URL has been added to the form.",
        });
         * 
         */
        
      },
      onError: (error: Error) => {
        toast({
          title: "Error uploading image",
          description: error.message || "Failed to upload the image. Please try again.",
          variant: "destructive",
        });
      },
    });

    // Handle image upload
    const handleImageUpload = () => {
      if (!selectedFile) {
        toast({
          title: "No file selected",
          description: "Please select a file to upload.",
          variant: "destructive",
        });
        return;
      }
      
      uploadImageMutation.mutate(selectedFile);
    };

    return (
      <div className="space-y-2">
        <Label htmlFor={id}>{label}</Label>
        <div className="flex gap-2">
          <Input
            id={id}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className="flex-1"
            ref={ref}
          />
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            className="hidden"
            accept="image/*"
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            className="whitespace-nowrap"
          >
            <ImageIcon className="h-4 w-4 mr-2" />
            Browse
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={handleImageUpload}
            disabled={!selectedFile || uploadImageMutation.isPending}
          >
            {uploadImageMutation.isPending ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <Upload className="h-4 w-4 mr-2" />
            )}
            Upload
          </Button>
        </div>
        {selectedFile && (
          <p className="text-xs text-muted-foreground">
            Selected: {selectedFile.name}
          </p>
        )}
      </div>
    );
  }
);

ImageUploadField.displayName = 'ImageUploadField';